import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { Router, NavigationError } from '@angular/router';

/**
 * Catches Angular lazy-load ChunkLoadErrors that occur either:
 *  1. During a router navigation (nav link click) — redirects to the target
 *     URL as a full page load so the browser fetches fresh chunks.
 *  2. At any other point (e.g. initial load) — does a hard reload.
 *
 * A sessionStorage flag prevents an infinite loop if the chunk is genuinely
 * missing from the server.
 */
@Injectable()
export class ChunkErrorHandler implements ErrorHandler {

  constructor(private ngZone: NgZone) {}

  private isChunkError(error: any): boolean {
    return (
      error?.name === 'ChunkLoadError' ||
      /Loading chunk .+ failed/i.test(error?.message ?? '') ||
      /Loading chunk .+ failed/i.test(error?.originalError?.message ?? '') ||
      /Loading chunk .+ failed/i.test(error?.rejection?.message ?? '')
    );
  }

  handleError(error: any): void {
    // Unwrap promise rejections (router wraps errors this way)
    const actual = error?.rejection ?? error;

    if (!this.isChunkError(actual)) {
      console.error(error);
      return;
    }

    const reloadKey = 'chunk_reload_attempted';
    const alreadyRetried = sessionStorage.getItem(reloadKey);

    if (alreadyRetried) {
      // Prevent infinite loop — chunk is genuinely missing
      sessionStorage.removeItem(reloadKey);
      console.error('[ChunkErrorHandler] Chunk load failed even after reload. File may be missing from server.', actual);
      return;
    }

    sessionStorage.setItem(reloadKey, 'true');

    // If the error carries a router URL (NavigationError), navigate there as a
    // hard redirect so the browser loads the fresh chunk for that route.
    const targetUrl: string | undefined = actual?.url ?? error?.url;

    if (targetUrl) {
      console.warn(`[ChunkErrorHandler] Chunk load failed navigating to "${targetUrl}" — redirecting for fresh load.`);
      this.ngZone.runOutsideAngular(() => {
        window.location.href = targetUrl;
      });
    } else {
      console.warn('[ChunkErrorHandler] Chunk load failed — reloading page for fresh bundles.');
      this.ngZone.runOutsideAngular(() => {
        window.location.reload();
      });
    }
  }
}

/**
 * Sets up a Router-level NavigationError listener so chunk errors during
 * lazy-route navigation are caught and trigger a hard redirect to the
 * target route URL.
 *
 * Call bootstrapChunkErrorRecovery(router) once in AppComponent.ngOnInit().
 */
export function bootstrapChunkErrorRecovery(router: Router): void {
  router.events.subscribe(event => {
    if (!(event instanceof NavigationError)) return;

    const error = event.error;
    const isChunk =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk .+ failed/i.test(error?.message ?? '');

    if (!isChunk) return;

    const reloadKey = 'chunk_reload_attempted';
    if (sessionStorage.getItem(reloadKey)) {
      sessionStorage.removeItem(reloadKey);
      console.error('[ChunkErrorHandler] Navigation chunk error persists after reload.', error);
      return;
    }

    sessionStorage.setItem(reloadKey, 'true');
    console.warn(`[ChunkErrorHandler] NavigationError chunk load for "${event.url}" — redirecting.`);
    window.location.href = event.url;
  });
}
